import * as React from 'react'
import { useState, useCallback, useEffect } from 'react'
import { Calendar } from '@/components/ui/range-calendar'
import { Button } from '@/components/ui/button'
import {
    subDays,
    subMonths,
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    addMonths,
    startOfDay,
    endOfDay,
} from 'date-fns'
import { cn } from '@/lib/utils'

const predefinedRanges = [
    {
        label: 'Lifetime',
        getValue: () => ({
            from: subMonths(new Date(), 37),
            to: new Date(),
        }),
    },
    {
        label: 'Today',
        getValue: () => ({
            from: startOfDay(new Date()),
            to: endOfDay(new Date()),
        }),
    },
    {
        label: 'Yesterday',
        getValue: () => {
            const yesterday = subDays(new Date(), 1)
            return {
                from: startOfDay(yesterday),
                to: endOfDay(yesterday),
            }
        },
    },
    {
        label: 'Last 3 days',
        getValue: () => ({
            from: subDays(new Date(), 2),
            to: new Date(),
        }),
    },
    {
        label: 'Last 7 days',
        getValue: () => ({
            from: subDays(new Date(), 6),
            to: new Date(),
        }),
    },
    {
        label: 'Last 14 days',
        getValue: () => ({
            from: subDays(new Date(), 13),
            to: new Date(),
        }),
    },
    {
        label: 'Last 30 days',
        getValue: () => ({
            from: subDays(new Date(), 30),
            to: new Date(),
        }),
    },
    {
        label: 'Last 60 days',
        getValue: () => ({
            from: subDays(new Date(), 59),
            to: new Date(),
        }),
    },
    {
        label: 'Last 90 days',
        getValue: () => ({
            from: subDays(new Date(), 89),
            to: new Date(),
        }),
    },
    {
        label: 'Last 180 days',
        getValue: () => ({
            from: subDays(new Date(), 179),
            to: new Date(),
        }),
    },
    {
        label: 'Last 365 days',
        getValue: () => ({
            from: subDays(new Date(), 364),
            to: new Date(),
        }),
    },
    {
        label: 'This week',
        getValue: () => ({
            from: startOfWeek(new Date(), { weekStartsOn: 1 }),
            to: new Date(),
        }),
    },
    {
        label: 'This month',
        getValue: () => ({
            from: startOfMonth(new Date()),
            to: new Date(),
        }),
    },
    {
        label: 'Last week',
        getValue: () => {
            const lastWeek = subDays(new Date(), 7)
            return {
                from: startOfWeek(lastWeek, { weekStartsOn: 1 }),
                to: endOfWeek(lastWeek, { weekStartsOn: 1 }),
            }
        },
    },
    {
        label: 'Last month',
        getValue: () => {
            const lastMonth = addMonths(new Date(), -1)
            return {
                from: startOfMonth(lastMonth),
                to: endOfMonth(lastMonth),
            }
        },
    },
]

const DateRangePicker = ({ value, onChange, onCancel }) => {
    const [selectedRange, setSelectedRange] = useState(value || { from: undefined, to: undefined })

    useEffect(() => {
        setSelectedRange(value || { from: undefined, to: undefined })
    }, [value])

    const handlePredefinedRangeSelect = useCallback((range) => {
        const newRange = range.getValue()
        setSelectedRange(newRange)
    }, [])

    const handleCalendarSelect = useCallback(
        (range, date) => {
            if (!range) return

            if (selectedRange?.from && selectedRange?.to) setSelectedRange({ from: date, to: undefined })
            else setSelectedRange(range)
        },
        [selectedRange],
    )

    const handleApply = useCallback(() => {
        if (selectedRange?.from && selectedRange?.to) onChange(selectedRange)
    }, [selectedRange, onChange])

    const isRangeComplete = selectedRange?.from && selectedRange?.to

    return (
        <div className="tw:flex tw:min-w-[600px]">
            {/* Left panel - Predefined ranges */}
            <div
                className={cn(
                    'tw:border-r tw:border-gray-200 tw:dark:border-[#3B5168] tw:p-3',
                    'tw:w-[140px] tw:bg-gray-50 tw:dark:bg-[#0A1A2A] tw:overflow-auto tw:max-h-[422px]',
                )}
            >
                <div className="tw:flex tw:flex-col">
                    {predefinedRanges.map((range) => (
                        <div
                            key={range.label}
                            className="tw:flex tw:items-center tw:gap-1.5 tw:hover:bg-gray-500 tw:rounded-lg tw:pl-2"
                        >
                            <Button
                                variant="link"
                                onClick={() => handlePredefinedRangeSelect(range)}
                                className="tw:text-white tw:text-left tw:hover:text-white tw:hover:no-underline tw:flex tw:justify-start tw:h-8"
                            >
                                {range.label}
                            </Button>
                        </div>
                    ))}
                </div>
            </div>

            {/* Right panel - Calendar */}
            <div className="tw:bg-white tw:dark:bg-[#071727]">
                {/* Calendar */}
                <div className="tw:flex tw:justify-center">
                    <Calendar
                        mode="range"
                        showOutsideDays
                        numberOfMonths={2}
                        selected={selectedRange}
                        onSelect={handleCalendarSelect}
                        disabled={{ after: new Date() }}
                        className="tw:rounded-md tw:border-0"
                    />
                </div>

                {/* Action buttons */}
                <div className="tw:flex tw:gap-2 tw:justify-end tw:border-t tw:p-3">
                    <Button variant="secondary" size="sm" onClick={onCancel} className="tw:w-[165px] tw:h-7">
                        Cancel
                    </Button>
                    <Button onClick={handleApply} size="sm" disabled={!isRangeComplete} className="tw:w-[165px] tw:h-7">
                        Apply
                    </Button>
                </div>
            </div>
        </div>
    )
}

export default DateRangePicker
